import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { FaRobot, FaTimes, FaPaperPlane, FaMicrophone, FaMicrophoneSlash, FaTrash, FaMinus, FaCrown, FaLock, FaExclamationTriangle, FaHourglassEnd } from 'react-icons/fa';
import { setOpen, toggleOpen, sendMessageStream, clearConversation, clearError, fetchAiPreferences, fetchAiStatus, fetchAiUsage } from '../../redux/slices/aiSlice';
import AiMessage from './AiMessage';
import SuggestedQuestions from './SuggestedQuestions';
import useTrialStatus from '../../hooks/useTrialStatus';
import { useOverlayBack } from '../../hooks/useSmartBack';
import { playOpenSound } from '../../utils/alertSound';
import './AiAssistant.scss';

function formatCount(n) {
  if (n >= 1000) return (n / 1000).toFixed(n >= 10000 ? 0 : 1).replace('.0', '') + 'k';
  return String(n).padStart(2, '0');
}

const AiAssistant = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const userAvatar = user?.avatar || user?.photoURL || null;
  const { conversations, loading, error, isOpen, conversationId, rateLimitRetryAfter, streamingContent, isAiDisabled, dailyUsed, dailyMax } = useSelector((state) => state.ai);
  const aiPrefs = useSelector((state) => state.ai?.preferences) || {};
  const speechLanguage = aiPrefs.speechLanguage || 'en-IN';
  const assistantName = aiPrefs.assistantVoice === 'male' ? 'Mivan' : 'Mira';
  const assistantGreeting = `Hi, I'm ${assistantName}, your personal assistant.`;
  const [inputText, setInputText] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [cooldown, setCooldown] = useState(0);
  const [showRequestInfo, setShowRequestInfo] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const panelRef = useRef(null);
  const recognitionRef = useRef(null);
  const trialStatus = useTrialStatus();

  // Load user's AI preferences and status on mount
  useEffect(() => {
    dispatch(fetchAiPreferences());
    dispatch(fetchAiStatus());
  }, [dispatch]);

  // Re-check status and usage when panel opens
  useEffect(() => {
    if (isOpen) {
      dispatch(fetchAiStatus());
      dispatch(fetchAiUsage());
    }
  }, [isOpen, dispatch]);

  // Step-by-step back navigation: a hardware / browser / swipe back press
  // while the panel is open closes the panel (one press = one layer) instead
  // of leaving the page. Handled by the shared overlay stack so stacked
  // modals + panels unwind one at a time.
  const closePanel = useCallback(() => dispatch(setOpen(false)), [dispatch]);
  useOverlayBack(isOpen, closePanel, 'ai-assistant');

  // Rate-limit cooldown countdown
  useEffect(() => {
    if (rateLimitRetryAfter > 0) {
      setCooldown(rateLimitRetryAfter);
    }
  }, [rateLimitRetryAfter]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => {
      setCooldown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          dispatch(clearError());
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldown > 0]);

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversations, loading]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Adjust panel height on mobile when keyboard opens/closes
  useEffect(() => {
    if (!isOpen || !panelRef.current) return;
    const panel = panelRef.current;

    const setPanelHeight = () => {
      if (window.visualViewport) {
        panel.style.setProperty('--ai-panel-height', `${window.visualViewport.height}px`);
      }
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', setPanelHeight);
      setPanelHeight();
    }

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', setPanelHeight);
      }
    };
  }, [isOpen]);

  const isLimitReached = dailyUsed >= dailyMax;

  const prevIsOpen = useRef(isOpen);
  useEffect(() => {
    if (prevIsOpen.current === isOpen) return;
    prevIsOpen.current = isOpen;
    if (isOpen) {
      playOpenSound();
    }
  }, [isOpen]);

  const handleSend = useCallback(async (text) => {
    const message = (text || inputText).trim();
    if (!message || loading || cooldown > 0 || isLimitReached) return;
    setInputText('');
    dispatch(sendMessageStream({ message, conversationId }));
  }, [inputText, loading, conversationId, dispatch, cooldown, isLimitReached]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const startListening = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = speechLanguage;

    recognition.onresult = (event) => {
      const transcript = Array.from(event.results)
        .map((result) => result[0].transcript)
        .join('');
      setInputText(transcript);
    };

    recognition.onend = () => {
      setIsListening(false);
      recognitionRef.current = null;
    };

    recognition.onerror = () => {
      setIsListening(false);
      recognitionRef.current = null;
    };

    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  };

  const stopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setIsListening(false);
  };

  const toggleListening = () => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  const handleClear = () => {
    if (conversations.length > 0) {
      dispatch(clearConversation());
    }
  };

  const hasSpeechInput = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);

  // Non-Gold users see a locked version with upgrade prompt
  if (!trialStatus.isGoldPlan) {
    return (
      <>
        {!isOpen ? (
          <button className="ai-fab" onClick={() => dispatch(toggleOpen())} title="AI Assistant" aria-label="AI Assistant">
            <FaRobot />
          </button>
        ) : (
          <>
            <button className="ai-fab ai-fab--active" onClick={() => dispatch(toggleOpen())} title="Close" aria-label="Close AI Assistant">
              <FaTimes />
            </button>
            <div className="ai-panel" ref={panelRef}>
              <div className="ai-panel__header">
                <div className="ai-panel__header-left">
                  <FaRobot className="ai-panel__header-icon" />
                  <div>
                    <h3 className="ai-panel__title">AI Assistant</h3>
                    <span className="ai-panel__subtitle">Gold plan feature</span>
                  </div>
                </div>
                <div className="ai-panel__header-actions">
                  <button className="ai-panel__header-btn" onClick={() => dispatch(setOpen(false))} title="Minimize">
                    <FaMinus />
                  </button>
                </div>
              </div>
              <div className="ai-panel__messages">
                <div className="ai-panel__welcome">
                  <div className="ai-panel__welcome-icon"><FaLock /></div>
                  <h4>Upgrade to Gold</h4>
                  <p>The AI Assistant is available exclusively on the Gold plan. Upgrade now to ask questions about your events, quotations, revenue, projects, and more.</p>
                  <button
                    className="ai-panel__upgrade-btn"
                    onClick={() => window.location.href = '/payment?plan=gold'}
                  >
                    <FaCrown /> Upgrade to Gold
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </>
    );
  }

  if (!isOpen) {
    return (
      <button className="ai-fab" onClick={() => dispatch(toggleOpen())} title="AI Assistant" aria-label="AI Assistant">
        <FaRobot />
      </button>
    );
  }

  return (
    <>
      <button className="ai-fab ai-fab--active" onClick={() => dispatch(toggleOpen())} title="Close AI Assistant">
        <FaTimes />
      </button>
      <div className="ai-panel" ref={panelRef}>
        <div className="ai-panel__header">
          <div className="ai-panel__header-left">
            <FaRobot className="ai-panel__header-icon" />
            <div>
              <h3 className="ai-panel__title">{assistantName}</h3>
            </div>
          </div>
          <div className="ai-panel__header-actions">
            <div
              className={`ai-panel__request-count ${isLimitReached ? 'ai-panel__request-count--limit' : ''}`}
              onClick={() => setShowRequestInfo(!showRequestInfo)}
              onMouseEnter={() => setShowRequestInfo(true)}
              onMouseLeave={() => setShowRequestInfo(false)}
            >
              {isLimitReached ? (
                <><FaHourglassEnd /> 0 / {formatCount(dailyMax)}</>
              ) : (
                <>{formatCount(dailyUsed)} / {formatCount(dailyMax)}</>
              )}
            </div>
            {conversations.length > 0 && !isLimitReached && (
              <button className="ai-panel__header-btn" onClick={handleClear} title="Clear conversation">
                <FaTrash />
              </button>
            )}
            <button className="ai-panel__header-btn" onClick={() => dispatch(setOpen(false))} title="Minimize">
              <FaMinus />
            </button>
          </div>
          {showRequestInfo && (
            <div className="ai-panel__request-tooltip">
              This is the per day request limit and resets every day at 1AM
            </div>
          )}
        </div>

        <div className={`ai-panel__offline-banner ${isOffline ? 'ai-panel__offline-banner--offline' : ''}`}>
          {isOffline ? <><FaExclamationTriangle /> You are offline</> : 'Powered by Gemini - AI can make mistakes'}
        </div>

        <div className="ai-panel__messages">
          {isOffline ? (
            <div className="ai-panel__welcome">
              <div className="ai-panel__welcome-icon ai-panel__welcome-icon--offline"><FaExclamationTriangle /></div>
              <h4>You are offline</h4>
              <p>The AI Assistant requires an active internet connection. Please check your network and reconnect to ask questions.</p>
            </div>
          ) : isAiDisabled ? (
            <div className="ai-panel__welcome">
              <div className="ai-panel__welcome-icon"><FaRobot /></div>
              <h4>AI Assistant Unavailable</h4>
              <p>The AI assistant is currently unavailable. Please contact us or check back later.</p>
            </div>
          ) : isLimitReached && conversations.length === 0 ? (
            <div className="ai-panel__welcome">
              <div className="ai-panel__welcome-icon ai-panel__welcome-icon--limit"><FaHourglassEnd /></div>
              <h4>Daily limit reached</h4>
              <p>You have used all {dailyMax} queries for today. Your limit resets at 1AM. Please come back after 1AM to continue using the AI Assistant.</p>
            </div>
          ) : conversations.length === 0 ? (
            <div className="ai-panel__welcome">
              <div className="ai-panel__welcome-icon"><FaRobot /></div>
              <h4>{assistantGreeting}</h4>
              <p>I can answer questions about your events, quotations, revenue, projects, team, and other data within the platform. I do not take any actions — I only help you find information.</p>
              <SuggestedQuestions onSelect={(text) => handleSend(text)} />
            </div>
          ) : (
            <>
              {conversations.map((msg) => (
                <AiMessage key={msg.id} message={msg} userAvatar={userAvatar} />
              ))}
            </>
          )}

          {error && (
            <div className="ai-panel__error">
              <span>{error}</span>
              <button onClick={() => dispatch(clearError())}><FaTimes /></button>
            </div>
          )}

          {loading && (
            <div className="ai-message ai-message--assistant">
              <div className="ai-message__avatar"><FaRobot /></div>
              <div className="ai-message__content">
                {streamingContent ? (
                  <div className="ai-message__text ai-message__text--streaming">
                    {streamingContent}
                    <span className="ai-streaming-cursor" />
                  </div>
                ) : (
                  <div className="ai-typing">
                    <span className="ai-typing__dot" />
                    <span className="ai-typing__dot" />
                    <span className="ai-typing__dot" />
                  </div>
                )}
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {!isAiDisabled && !isOffline && !isLimitReached && (
          <div className="ai-panel__input">
            <div className="ai-panel__input-wrapper">
              <textarea
                ref={inputRef}
                className="ai-panel__textarea"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={cooldown > 0 ? `Wait ${cooldown}s...` : "Ask me anything..."}
                rows={2}
                disabled={loading || cooldown > 0}
              />
              {hasSpeechInput && (
                <button
                  className={`ai-panel__mic-btn ${isListening ? 'ai-panel__mic-btn--listening' : ''}`}
                  onClick={toggleListening}
                  disabled={loading || cooldown > 0}
                  title={isListening ? 'Stop listening' : 'Voice input'}
                >
                  {isListening ? <FaMicrophoneSlash /> : <FaMicrophone />}
                </button>
              )}
              <button
                className="ai-panel__send-btn"
                onClick={() => handleSend()}
                disabled={!inputText.trim() || loading || cooldown > 0}
                title={cooldown > 0 ? `Wait ${cooldown}s` : "Send"}
              >
                <FaPaperPlane />
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AiAssistant;
